import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Zap, Plus, Trash2, Pencil, Send, Loader2, Copy, CheckCircle2, XCircle,
  MessageSquare, FlaskConical, Info, ChevronDown, ChevronUp,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = {
  name: "", keywords: "", match_type: "contains", dm_text: "",
  public_reply_enabled: false, public_reply_text: "Schau in deine DMs! 📩",
  media_id: null, media_label: null, active: true,
};

export default function DmAutomation() {
  const outlet = useOutletContext();
  const accountKey = outlet?.accountKey;
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [setup, setSetup] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [testText, setTestText] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const [r, l, s] = await Promise.all([
        api.get("/dm-rules", { params: { account: accountKey } }),
        api.get("/dm-automation/logs", { params: { account: accountKey } }),
        api.get("/dm-automation/setup", { params: { account: accountKey } }),
      ]);
      setRules(r.data); setLogs(l.data); setSetup(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "DM-Automation konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accountKey) return;
    setLoading(true);
    load();
    api.get("/dm-automation/media", { params: { account: accountKey } })
      .then(({ data }) => setMedia(data.media || [])).catch(() => {});
  }, [accountKey]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (rule) => {
    setEditing(rule);
    setForm({ ...rule, keywords: (rule.keywords || []).join(", ") });
    setDialogOpen(true);
  };

  const save = async () => {
    const keywords = form.keywords.split(",").map((k) => k.trim()).filter(Boolean);
    if (!form.name.trim()) return toast.error("Bitte einen Namen angeben");
    if (!keywords.length) return toast.error("Mindestens ein Keyword angeben");
    if (!form.dm_text.trim()) return toast.error("DM-Text darf nicht leer sein");
    setSaving(true);
    try {
      const payload = {
        name: form.name, keywords, match_type: form.match_type, dm_text: form.dm_text,
        public_reply_enabled: form.public_reply_enabled, public_reply_text: form.public_reply_text,
        media_id: form.media_id || null, media_label: form.media_label || null, active: form.active,
      };
      if (editing) {
        await api.patch(`/dm-rules/${editing.id}`, { ...payload, clear_media: !form.media_id });
        toast.success("Regel aktualisiert");
      } else {
        await api.post("/dm-rules", { ...payload, account_key: accountKey });
        toast.success("Regel erstellt");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const toggleActive = async (rule) => {
    try {
      await api.patch(`/dm-rules/${rule.id}`, { active: !rule.active });
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status konnte nicht geändert werden");
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Regel "${rule.name}" wirklich löschen?`)) return;
    await api.delete(`/dm-rules/${rule.id}`);
    toast.success("Regel gelöscht");
    load();
  };

  const runTest = async () => {
    if (!testText.trim()) return;
    setTesting(true);
    try {
      const { data } = await api.post("/dm-automation/simulate", {
        account_key: accountKey, comment_text: testText,
      });
      setTestResult(data);
    } catch { toast.error("Test fehlgeschlagen"); }
    finally { setTesting(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Kopiert"); }
    catch { toast.error("Kopieren fehlgeschlagen — bitte manuell markieren"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div data-testid="dm-automation-page" className="max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400 flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5" /> Marketing-Automation
          </p>
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight mt-1">DM-Automation</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Kommentiert jemand ein Keyword unter deinen Beiträgen, bekommt er automatisch eine private DM — wie bei ManyChat, nur kostenlos.
          </p>
        </div>
        <button
          data-testid="dm-rule-create-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 transition shrink-0"
        >
          <Plus className="h-4 w-4" /> Neue Regel
        </button>
      </div>

      {/* Setup / Status */}
      <div className="mt-6 rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <button
          data-testid="dm-setup-toggle"
          onClick={() => setShowSetup((s) => !s)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <div className="flex items-center gap-2.5">
            <Info className="h-4 w-4 text-neutral-400" />
            <span className="text-sm font-semibold text-neutral-800">Einrichtung & Status</span>
            {setup?.ready ? (
              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">Bereit</span>
            ) : (
              <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Aktion nötig</span>
            )}
          </div>
          {showSetup ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
        </button>
        {showSetup && (
          <div className="px-4 pb-4 space-y-3 text-sm text-neutral-600 border-t border-neutral-100 pt-3">
            <div className="flex items-start gap-2">
              {setup?.connected ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />}
              <p>
                <strong>1. Instagram verbunden:</strong>{" "}
                {setup?.connected ? `Ja (@${setup.ig_username})` : "Nein — verbinde dein Konto unter Einstellungen."}
              </p>
            </div>
            <div className="flex items-start gap-2">
              {setup?.has_message_scope ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
              <p>
                <strong>2. Nachrichten-Berechtigung:</strong>{" "}
                {setup?.has_message_scope
                  ? "Vorhanden."
                  : "Fehlt noch — verbinde Instagram unter Einstellungen einmal neu, damit die neue Berechtigung (Nachrichten senden) erteilt wird."}
              </p>
            </div>
            <div className="flex items-start gap-2">
              {setup?.backend_configured ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />}
              <p><strong>3. Server-Konfiguration:</strong> {setup?.backend_configured ? "Vollständig." : "Meta-App-ID, App-Secret oder Webhook-Token fehlen auf dem Server."}</p>
            </div>
            <div className="flex items-start gap-2">
              {setup?.webhook_verified ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
              <p><strong>4. Meta-Webhook:</strong> {setup?.webhook_verified ? "Von Meta verifiziert." : "Noch nicht von Meta verifiziert."}</p>
            </div>
            <div>
              <p className="font-semibold text-neutral-800 mb-1.5">Webhook in der Meta-Developer-Konsole eintragen (einmalig):</p>
              <p className="mb-2">App-Dashboard → Instagram → Webhooks einrichten → Feld <strong>comments</strong> abonnieren, dann:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <code data-testid="dm-webhook-url" className="flex-1 text-xs bg-neutral-50 border border-neutral-200 rounded-md px-2.5 py-1.5 truncate">{setup?.webhook_url}</code>
                  <button data-testid="dm-copy-webhook-url" onClick={() => copy(setup?.webhook_url)} className="p-2 rounded-md hover:bg-neutral-100"><Copy className="h-4 w-4 text-neutral-500" /></button>
                </div>
                {setup?.verify_token && (
                  <div className="flex items-center gap-2">
                    <code data-testid="dm-verify-token" className="flex-1 text-xs bg-neutral-50 border border-neutral-200 rounded-md px-2.5 py-1.5 truncate">Verifizierungs-Token: {setup.verify_token}</code>
                    <button data-testid="dm-copy-verify-token" onClick={() => copy(setup.verify_token)} className="p-2 rounded-md hover:bg-neutral-100"><Copy className="h-4 w-4 text-neutral-500" /></button>
                  </div>
                )}
              </div>
              {(setup?.queue?.pending > 0 || setup?.queue?.attention > 0) && (
                <p data-testid="dm-queue-status" className="mt-2 text-xs text-neutral-500">
                  Warteschlange: {setup.queue.pending} offen · {setup.queue.attention} benötigen Prüfung
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Regeln */}
      <div className="mt-6">
        <h2 className="text-base md:text-lg font-bold text-neutral-900 mb-3">Regeln ({rules.length})</h2>
        {rules.length === 0 ? (
          <div data-testid="dm-rules-empty" className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-10 text-center">
            <MessageSquare className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-neutral-600">Noch keine Regeln</p>
            <p className="text-sm text-neutral-400 mt-1">Erstelle z.B.: Keyword „GUIDE“ → DM mit deinem Link.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {rules.map((rule) => (
              <div key={rule.id} data-testid={`dm-rule-card-${rule.id}`} className={`rounded-xl border bg-white p-4 ${rule.active ? "border-neutral-200" : "border-neutral-200 opacity-60"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-neutral-900 text-sm truncate">{rule.name}</p>
                    <p className="text-[11px] text-neutral-400 mt-0.5">
                      {rule.media_id ? `Nur Beitrag: ${rule.media_label || rule.media_id}` : "Alle Beiträge"} · {rule.match_type === "exact" ? "Exakte Übereinstimmung" : "Enthält Keyword"}
                    </p>
                  </div>
                  <Switch data-testid={`dm-rule-toggle-${rule.id}`} checked={rule.active} onCheckedChange={() => toggleActive(rule)} />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(rule.keywords || []).map((k) => (
                    <span key={k} className="text-[11px] font-bold uppercase tracking-wide bg-neutral-900 text-white px-2 py-0.5 rounded-full">{k}</span>
                  ))}
                </div>
                <p className="text-sm text-neutral-600 mt-3 line-clamp-3 whitespace-pre-line">{rule.dm_text}</p>
                {rule.public_reply_enabled && (
                  <p className="text-[11px] text-neutral-400 mt-2">Öffentliche Antwort: „{rule.public_reply_text}“</p>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
                  <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                    <Send className="h-3.5 w-3.5" /> {rule.sent_count || 0} DMs gesendet
                  </span>
                  <div className="flex items-center gap-1">
                    <button data-testid={`dm-rule-edit-${rule.id}`} onClick={() => openEdit(rule)} className="p-1.5 rounded-md hover:bg-neutral-100"><Pencil className="h-4 w-4 text-neutral-500" /></button>
                    <button data-testid={`dm-rule-delete-${rule.id}`} onClick={() => remove(rule)} className="p-1.5 rounded-md hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-500" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tester */}
      <div className="mt-8 rounded-xl border border-neutral-200 bg-white p-4">
        <h2 className="text-sm font-bold text-neutral-900 flex items-center gap-2"><FlaskConical className="h-4 w-4" /> Regel-Tester</h2>
        <p className="text-xs text-neutral-500 mt-1">Simuliere einen Kommentar und prüfe, welche Regel greifen würde (es wird nichts gesendet).</p>
        <div className="flex gap-2 mt-3">
          <Input
            data-testid="dm-test-input"
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runTest()}
            placeholder='z.B. "GUIDE bitte!"'
          />
          <button
            data-testid="dm-test-run-btn"
            onClick={runTest}
            disabled={testing || !testText.trim()}
            className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 rounded-lg hover:bg-neutral-800 transition disabled:opacity-50 shrink-0"
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Testen"}
          </button>
        </div>
        {testResult && (
          <div data-testid="dm-test-result" className={`mt-3 rounded-lg border p-3 text-sm ${testResult.matched ? "border-emerald-200 bg-emerald-50" : "border-neutral-200 bg-neutral-50"}`}>
            {testResult.matched ? (
              <>
                <p className="font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Regel „{testResult.rule_name}“ greift</p>
                <p className="text-neutral-600 mt-1.5 whitespace-pre-line"><strong>DM:</strong> {testResult.dm_text}</p>
                {testResult.public_reply && <p className="text-neutral-600 mt-1"><strong>Öffentliche Antwort:</strong> {testResult.public_reply}</p>}
              </>
            ) : (
              <p className="text-neutral-500">Keine aktive Regel passt auf diesen Kommentar.</p>
            )}
          </div>
        )}
      </div>

      {/* Logs */}
      <div className="mt-8">
        <h2 className="text-base md:text-lg font-bold text-neutral-900 mb-3">DM-Aktivitäten ({logs.length})</h2>
        {logs.length === 0 ? (
          <p data-testid="dm-logs-empty" className="text-sm text-neutral-400">Noch keine automatischen DMs versendet.</p>
        ) : (
          <div className="rounded-xl border border-neutral-200 bg-white divide-y divide-neutral-100">
            {logs.map((log) => (
              <div key={log.id} data-testid={`dm-log-${log.id}`} className="px-4 py-3 flex items-start gap-3">
                {log.dm_ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className={`h-4 w-4 mt-0.5 shrink-0 ${log.delivery_status === "uncertain" ? "text-amber-500" : "text-red-500"}`} />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-neutral-800">
                    <strong>@{log.commenter || "unbekannt"}</strong> kommentierte: „{log.comment_text}“
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    Regel „{log.rule_name}“ · {log.created_at ? format(parseISO(log.created_at), "dd.MM.yyyy HH:mm", { locale: de }) : ""}
                    {log.delivery_status === "uncertain" ? " · Versandstatus unklar — bitte Instagram prüfen" : ""}
                    {!log.dm_ok && log.error ? ` · Fehler: ${log.error}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Regel bearbeiten" : "Neue DM-Regel"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name der Regel</Label>
              <Input data-testid="dm-form-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Guide-Versand" />
            </div>
            <div>
              <Label>Keywords (kommagetrennt)</Label>
              <Input data-testid="dm-form-keywords" className="mt-1.5" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="GUIDE, LINK, INFO" />
            </div>
            <div>
              <Label>Übereinstimmung</Label>
              <Select value={form.match_type} onValueChange={(v) => setForm({ ...form, match_type: v })}>
                <SelectTrigger data-testid="dm-form-match-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">Kommentar enthält das Keyword</SelectItem>
                  <SelectItem value="exact">Kommentar ist exakt das Keyword</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gilt für</Label>
              <Select
                value={form.media_id || "all"}
                onValueChange={(v) => {
                  const m = media.find((x) => x.id === v);
                  setForm({ ...form, media_id: v === "all" ? null : v, media_label: m ? m.caption : null });
                }}
              >
                <SelectTrigger data-testid="dm-form-media" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Beiträge</SelectItem>
                  {media.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.type === "REELS" ? "🎬 " : "📷 "}{m.caption}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {media.length === 0 && <p className="text-[11px] text-neutral-400 mt-1">Beitragsliste verfügbar, sobald Instagram verbunden ist.</p>}
            </div>
            <div>
              <Label>DM-Text (private Nachricht)</Label>
              <Textarea
                data-testid="dm-form-dm-text"
                className="mt-1.5 min-h-[110px]"
                value={form.dm_text}
                onChange={(e) => setForm({ ...form, dm_text: e.target.value })}
                placeholder={"Hey {username}! 👋 Hier ist dein Guide:\nhttps://dein-link.de"}
              />
              <p className="text-[11px] text-neutral-400 mt-1">Tipp: {"{username}"} wird durch den Namen des Kommentators ersetzt. Links einfach in den Text schreiben.</p>
            </div>
            <div className="rounded-lg border border-neutral-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-800">Öffentlich auf den Kommentar antworten</p>
                  <p className="text-[11px] text-neutral-400">z.B. „Schau in deine DMs! 📩“ — steigert die Sichtbarkeit</p>
                </div>
                <Switch data-testid="dm-form-public-reply-toggle" checked={form.public_reply_enabled} onCheckedChange={(v) => setForm({ ...form, public_reply_enabled: v })} />
              </div>
              {form.public_reply_enabled && (
                <Input data-testid="dm-form-public-reply-text" className="mt-3" value={form.public_reply_text} onChange={(e) => setForm({ ...form, public_reply_text: e.target.value })} />
              )}
            </div>
            <div className="flex items-center justify-between">
              <Label>Regel aktiv</Label>
              <Switch data-testid="dm-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <button
              data-testid="dm-form-save-btn"
              onClick={save}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 transition disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Änderungen speichern" : "Regel erstellen"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
